/* Verkleinerte Fassungen eines Spielerfotos ablegen und nachziehen.
 *
 * - storeVariants: erzeugt die Fassungen aus dem Original (WebP oder PNG, beide mit
 *   Transparenz, siehe useImageVariants.js) und legt sie neben dem Original ab
 *   (derivedPath, Endung nach dem tatsächlich gelieferten Typ). Ein Fehler hier ist
 *   KEIN Fehler des Uploads: Das Original steht, die Anzeige fällt dann auf das
 *   Original zurück.
 * - useVariantBackfill (nur admin, siehe PhotoOptimizeDialog.vue): geht alle Spieler
 *   durch. Läuft im Browser mit den Rechten des angemeldeten Nutzers. Das Original
 *   wird nie verändert, überschrieben oder gelöscht; überschrieben werden darf
 *   höchstens eine abgeleitete Fassung (uploadFileAt, `overwrite`). Es wird auch nichts
 *   gelöscht: Alte JPEG-Fassungen (weißer Hintergrund) bleiben liegen, werden aber nicht
 *   mehr angezeigt, sobald es eine neuere gibt (domain/photoVariants.js VARIANT_FORMATS).
 *
 *   Ohne "Alle neu erzeugen" fehlende Fassungen nachziehen (wie bisher); mit dem Haken
 *   für alle Fotos alle Fassungen neu erzeugen — so werden auch die alten JPEG-Fassungen
 *   ersetzt.
 */

import { ref } from 'vue';
import { makeVariants } from './useImageVariants.js';
import { downloadBlob, existsMany, uploadFileAt } from '../supabase/storage.js';
import {
  VARIANT_NAMES, VARIANT_FORMATS, derivedPath, photoTargets, variantState, photoState,
} from '../domain/photoVariants.js';

/**
 * Fassungen aus `blob` erzeugen und ablegen. names: nur diese (Vorgabe alle).
 * options.overwrite: eine schon vorhandene Fassung ersetzen (nur "Alle neu erzeugen").
 * options.make / options.put: austauschbar für Tests.
 * Liefert { created: [Namen], failed: [Namen] }.
 */
export async function storeVariants(blob, originalPath, names = VARIANT_NAMES, options = {}) {
  const { overwrite = false, make = makeVariants, put = uploadFileAt } = options;
  const made = await make(blob, names);
  const created = [];
  const failed = [];
  for (const name of names) {
    try {
      const v = made[name];
      await put(derivedPath(originalPath, name, v.ext), v.blob, {
        overwrite,
        contentType: v.contentType,
      });
      created.push(name);
    } catch {
      failed.push(name);
    }
  }
  return { created, failed };
}

const CHUNK = 90;

/**
 * Welche Fassungen gibt es? Für jede Datei { path, names, states: {name: 'current'|'legacy'|'missing'},
 * state }. Prüft je Fassung alle Endungen (webp, png, jpg) in wenigen Sammelabfragen.
 */
export async function inspectTargets(targets, exists = existsMany) {
  const paths = [];
  for (const t of targets) {
    for (const name of t.names) for (const ext of VARIANT_FORMATS) paths.push(derivedPath(t.path, name, ext));
  }
  const found = [];
  for (let i = 0; i < paths.length; i += CHUNK) found.push(...(await exists(paths.slice(i, i + CHUNK))));

  let k = 0;
  return targets.map((t) => {
    const states = {};
    for (const name of t.names) {
      const present = {};
      for (const ext of VARIANT_FORMATS) present[ext] = found[k++];
      states[name] = variantState(present);
    }
    return { ...t, states, state: photoState(Object.values(states)) };
  });
}

export function useVariantBackfill() {
  const running = ref(false);
  const finished = ref(false);
  const checking = ref(false);
  const total = ref(0);
  const done = ref(0);
  const created = ref(0);
  const skipped = ref(0);
  const failed = ref(0);
  /* Ergebnis der Vorabprüfung: Anzahl Fotodateien je Zustand (null = noch nicht geprüft) */
  const report = ref(null);

  const targetsOf = (players) =>
    players.flatMap((p) => photoTargets(p).map((t) => ({ id: p.id, ...t })));

  /**
   * Vorab zählen (nur lesend, es wird nichts verändert): wie viele Fotodateien haben
   * aktuelle Fassungen (mit Transparenz), nur die alten JPEG-Fassungen (weißer
   * Hintergrund) oder gar keine.
   */
  async function check(players) {
    checking.value = true;
    try {
      const inspected = await inspectTargets(targetsOf(players));
      const count = (s) => inspected.filter((t) => t.state === s).length;
      report.value = {
        total: inspected.length,
        current: count('current'),
        legacy: count('legacy'),
        missing: count('missing'),
      };
    } catch (e) {
      console.error('[photos] Prüfung fehlgeschlagen:', e && e.message);
      report.value = null;
    } finally {
      checking.value = false;
    }
  }

  /**
   * Alle Fotodateien der Spieler (avatarPath: thumb und md; bioPhotoPath, Altbestand:
   * md — domain/photoVariants.js photoTargets). Gezählt wird je Datei, nicht je Spieler.
   * options.force: "Alle neu erzeugen" — alle Fassungen aller Dateien neu, vorhandene
   * Fassungen werden ersetzt. Ohne: nur fehlende Fassungen.
   */
  async function run(players, { force = false } = {}) {
    if (running.value) return;
    const targets = targetsOf(players);
    running.value = true;
    finished.value = false;
    total.value = targets.length;
    done.value = created.value = skipped.value = failed.value = 0;

    for (const p of targets) {
      try {
        let names = p.names;
        if (!force) {
          const [info] = await inspectTargets([p]);
          names = p.names.filter((n) => info.states[n] === 'missing');
        }
        if (!names.length) {
          skipped.value++;
        } else {
          const original = await downloadBlob(p.path);
          const r = await storeVariants(original, p.path, names, { overwrite: force });
          if (r.failed.length) failed.value++;
          else created.value++;
        }
      } catch (e) {
        console.error('[photos] Nachziehen fehlgeschlagen:', p.id, e && e.message);
        failed.value++;
      }
      done.value++;
    }

    running.value = false;
    finished.value = true;
    /* Zahlen der Vorabprüfung sind nach dem Lauf veraltet */
    report.value = null;
  }

  return {
    running, finished, checking, total, done, created, skipped, failed, report, check, run,
  };
}
